import { InstantSearchFieldAdaptor } from '@/components/SearchField';
import { OutstandingDateFilterSelect } from '@/components/selects/OutstandingDateSelect';
import { OwnerMultiSelect } from '@/components/selects/OwnerSelect';
import { RefinementListSelect } from '@/components/selects/RefinementListSelect';
import { setFilters } from '@/features/table/instantSearchSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useInstantSearchOwnerFilters } from '@/hooks/useInstantSearchOwnerFilters';
import { outStandingDaysObj } from '@/utils/constants';
import { FormControl, FormLabel, Grid } from '@mui/material';
import { subDays } from 'date-fns';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GENERAL_FLEX, getGridItemProps } from '@/components/table/filterGrid';

const SEARCH_FLEX = 4;

const DebtorsTableFilters = ({
	outStandingDaysRange,
	setOutStandingDaysRange,
	outStandingDaysRangeValue,
	setOutStandingDaysRangeValue,
	hideStageFilter = false,
}) => {
	const dispatch = useDispatch();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const userDetailsUserId = userDetails?.userId;

	const { owners, slug } = useInstantSearchOwnerFilters();

	const getOutstandingDaysTimestamps = (selectedRange, asAt = new Date()) => {
		if (!selectedRange) return [null, null];
		const range = outStandingDaysObj[selectedRange];
		if (!range) return [null, null];
		const [minDays, maxDays] = range;
		const startDate = minDays !== null ? subDays(asAt, minDays) : null;
		const endDate = maxDays !== null ? subDays(asAt, maxDays) : null;
		const startTs = startDate ? startDate.getTime() : null;
		const endTs = endDate ? endDate.getTime() : null;

		return [startTs, endTs];
	};

	const handleOutstandingDaysChange = (selectedRange) => {
		if (!selectedRange) {
			setOutStandingDaysRange(null);
			setOutStandingDaysRangeValue(null);
			return;
		}

		const [startTs, endTs] = getOutstandingDaysTimestamps(selectedRange);
		setOutStandingDaysRange([startTs, endTs]);
		setOutStandingDaysRangeValue(selectedRange);
	};

	const searchGridProps = useMemo(() => getGridItemProps(SEARCH_FLEX), []);
	const generalGridProps = useMemo(() => getGridItemProps(GENERAL_FLEX), []);

	return (
		<Grid
			container
			columns={10}
			spacing={1}
			sx={{ pb: 2 }}
		>
			<Grid {...searchGridProps}>
				<FormControl fullWidth>
					<FormLabel>{'Search'}</FormLabel>
					<InstantSearchFieldAdaptor />
				</FormControl>
			</Grid>
			<Grid {...generalGridProps}>
				<FormControl fullWidth>
					<FormLabel>{'Assignee'}</FormLabel>
					<OwnerMultiSelect
						users={owners}
						disabled={!userDetails?.canViewGroup}
						isUserFixed={(user) => {
							if (user.id === userDetailsUserId) return owners.length === 1;
							return false;
						}}
						onChange={(value) => {
							const newIds = [value].flat();
							dispatch(
								setFilters({
									slug,
									type: 'owners',
									values: newIds.length === 0 ? [userDetailsUserId] : newIds,
								}),
							);
						}}
						returnValue='id'
						disableClearable={true}
					/>
				</FormControl>
			</Grid>
			{!hideStageFilter && (
				<Grid {...generalGridProps}>
					<RefinementListSelect
						attribute='dealStage'
						title='Status'
						multiple
						sx={{ width: '100%' }}
					/>
				</Grid>
			)}
			<Grid {...generalGridProps}>
				<FormControl fullWidth>
					<FormLabel>{'Outstanding days'}</FormLabel>
					<OutstandingDateFilterSelect
						outstandingDate={outStandingDaysRangeValue}
						onChange={handleOutstandingDaysChange}
					/>
				</FormControl>
			</Grid>
		</Grid>
	);
};
export default DebtorsTableFilters;
